// The facet model (guide 20.1): every view exposes the same facets, and ONE
// filter object drives the drawing, the tables, the summary, the tracking
// panel and the exports. Nothing filters on its own.
//
//   OR within a facet, AND across facets: "2 in OR 4 in" AND "telecom" AND
//   "Level 3" is one filter state.
//   Counts are live: each value's count is taken against the OTHER active
//   facets, so a user sees "4 in (10)" before clicking.
//   A facet with only one value is hidden rather than shown.
//   The state lives in the URL (guide 20.6), e.g.
//   #level=3&sys=telecom,security&size=2,2%2B4&cat=sleeve,riser&conf=measured,estimated
//
// A saved selection set (guide 19.2) keeps one of these filter objects next
// to the element keys it picked.

// "10'-6\" AFF" -> the band it sits in (guide 20.1: below slab, floor and
// walls, ceiling plane, telecom band, ductwork, piping).
export function bandOf(row) {
  const h = String((row && row.height) || "").toLowerCase();
  if (!h) return "";
  if (/below|slab|under/.test(h)) return "Below slab";
  const ft = h.match(/(\d+)\s*'\s*(?:-?\s*(\d+(?:\.\d+)?)\s*")?/);
  const inches = !ft && h.match(/(\d+(?:\.\d+)?)\s*"/);
  if (ft || inches) {
    const v = ft ? Number(ft[1]) + (ft[2] ? Number(ft[2]) / 12 : 0) : Number(inches[1]) / 12;
    if (v < 7.5) return "Floor and walls";
    if (v >= 10 && v <= 12) return "Telecom band";
    if (v > 12) return "Above ceiling";
    return "Ceiling plane";
  }
  if (/ceiling/.test(h)) return "Ceiling plane";
  if (/wall|floor/.test(h)) return "Floor and walls";
  return "";
}

const roomsOf = (r) => [...new Set([r.from, r.to, r.serves].filter((x) => x && typeof x === "string"))];
const sizeOf = (r) => String(r.size || "").replace(/\s+/g, "") || "not stated";

// key: the filter object's field; url: its short name in the hash; values: what a row contributes.
export const FACETS = [
  { key: "systems", url: "sys", label: "System", values: (r) => [r.system || "unassigned"] },
  { key: "sizes", url: "size", label: "Size", values: (r) => [sizeOf(r)] },
  { key: "categories", url: "cat", label: "Category", values: (r) => [r.type || "unnamed"] },
  { key: "levels", url: "level", label: "Level", values: (r) => [r.level == null || r.level === "" ? "not located" : String(r.level)] },
  { key: "zones", url: "zone", label: "Zone", values: (r) => (r.zone == null || r.zone === "" ? [] : [String(r.zone)]) },
  { key: "bands", url: "band", label: "Height band", values: (r) => { const b = bandOf(r); return b ? [b] : []; } },
  { key: "rooms", url: "room", label: "Room", values: roomsOf },
  { key: "tags", url: "tag", label: "Keynote or tag", values: (r) => (Array.isArray(r.keynotes) ? r.keynotes : []).map(String) },
  { key: "status", url: "status", label: "Status", values: (r, ctx) => (ctx && ctx.statusOf ? [ctx.statusOf(r)] : []), trackingOnly: true },
  { key: "confidence", url: "conf", label: "Confidence", values: (r) => (r.confidenceWord ? [r.confidenceWord] : []) },
];

export const emptyFilter = () => Object.fromEntries(FACETS.map((f) => [f.key, null]));

const active = (filter, key) => Array.isArray(filter && filter[key]) && filter[key].length > 0;

function passes(row, filter, ctx, skipKey) {
  for (const f of FACETS) {
    if (f.key === skipKey || !active(filter, f.key)) continue;
    const vals = f.values(row, ctx);
    if (!vals.some((v) => filter[f.key].includes(v))) return false;
  }
  return true;
}

/** The rows a filter keeps. */
export function applyFilter(rows, filter, ctx) {
  return (rows || []).filter((r) => passes(r, filter, ctx, null));
}

/**
 * Each facet's values with live counts - a value's count is the rows it would
 * keep given every OTHER active facet. Facets with fewer than two values are
 * left out (a tracking-only facet also needs ctx.statusOf).
 */
export function facetCounts(rows, filter, ctx) {
  return FACETS.filter((f) => !f.trackingOnly || (ctx && ctx.statusOf)).map((f) => {
    const all = new Map();
    const live = new Map();
    (rows || []).forEach((r) => {
      const vals = f.values(r, ctx);
      vals.forEach((v) => all.set(v, 0));
      if (passes(r, filter, ctx, f.key)) vals.forEach((v) => live.set(v, (live.get(v) || 0) + 1));
    });
    const values = [...all.keys()].sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))
      .map((v) => ({ value: v, count: live.get(v) || 0, on: active(filter, f.key) && filter[f.key].includes(v) }));
    return { ...f, values };
  }).filter((f) => f.values.length > 1);
}

/** Guide 20.6: the filter in the URL hash. Only active facets are written. */
export function filterToHash(filter) {
  const parts = FACETS.filter((f) => active(filter, f.key))
    .map((f) => `${f.url}=${filter[f.key].map((v) => encodeURIComponent(v)).join(",")}`);
  return parts.length ? `#${parts.join("&")}` : "";
}

export function filterFromHash(hash) {
  const out = emptyFilter();
  String(hash || "").replace(/^#/, "").split("&").filter(Boolean).forEach((p) => {
    const [k, v] = p.split("=");
    const f = FACETS.find((x) => x.url === k);
    if (f && v) out[f.key] = v.split(",").map((x) => { try { return decodeURIComponent(x); } catch (e) { return x; } }).filter(Boolean);
  });
  return out;
}

export const isEmptyFilter = (filter) => !FACETS.some((f) => active(filter, f.key));

/** Guide 19.2's footer: count, quantity and total length of a set of rows. */
export function tally(rows) {
  let qty = 0; let lengthFt = 0; let measured = 0;
  (rows || []).forEach((r) => {
    qty += r.qty || 1;
    if (r.lengthFt != null) { lengthFt += r.lengthFt * (r.qty || 1); measured += 1; }
  });
  return { rows: (rows || []).length, qty, lengthFt: Math.round(lengthFt), measured };
}

/**
 * Guide 20.1's empty state: say WHY nothing is shown - nothing of that kind
 * is drawn there, or the drawings for it were never received. A filter that
 * names a system the view has no rows of at all is the second.
 */
export function emptyReason(rows, filter) {
  if (!isEmptyFilter(filter) && active(filter, "systems")) {
    const present = new Set((rows || []).map((r) => r.system || "unassigned"));
    const missing = filter.systems.filter((s) => !present.has(s));
    if (missing.length === filter.systems.length) return `No drawings for ${missing.join(", ")} were received for this view, so nothing of it can be shown.`;
  }
  return "Nothing of this kind is drawn there. The drawings were received; they show none with this combination.";
}
